/**
 * Test kit cart lines: extract persons from flat line properties,
 * optional legacy JSON, list HTML for drawer updates, hydration.
 */
(function () {
  "use strict";

  /**
   * Shopify may return line `properties` as an object or as an array of
   * { name, value } pairs. Normalize to a string-keyed object.
   *
   * @param {unknown} raw
   * @returns {Record<string, string>}
   */
  function normalizeCartLineProperties(raw) {
    if (raw == null) {
      return {};
    }
    if (Array.isArray(raw)) {
      const out = {};
      raw.forEach((entry) => {
        if (!entry || typeof entry !== "object") {
          return;
        }
        const name = entry.name != null ? String(entry.name) : "";
        if (!name) {
          return;
        }
        out[name] =
          entry.value != null && entry.value !== ""
            ? String(entry.value)
            : "";
      });
      return out;
    }
    if (typeof raw === "object") {
      return /** @type {Record<string, string>} */ (raw);
    }
    return {};
  }

  const TK_FIELDS = [
    "title",
    "first_name",
    "last_name",
    "full_name",
    "email",
    "phone",
    "gender",
    "dob",
    "same_as_delivery_address",
    "address_1",
    "address_2",
    "town",
    "state",
    "postcode",
  ];

  /**
   * One cart line per person: `_test_kit_email`, `_test_kit_first_name`, …
   *
   * @param {Record<string, unknown>} properties
   * @returns {object[] | null}
   */
  function extractPersonsUnnumbered(properties) {
    if (!properties || typeof properties !== "object") {
      return null;
    }
    const email = properties._test_kit_email ?? properties["_test_kit_email"];
    if (email == null || String(email).trim() === "") {
      return null;
    }
    const p = {};
    TK_FIELDS.forEach((f) => {
      const k = `_test_kit_${f}`;
      const v = properties[k];
      if (f === "same_as_delivery_address") {
        p[f] = v === true || v === "true";
      } else {
        p[f] = v != null ? String(v) : "";
      }
    });
    return [p];
  }

  /**
   * Legacy: multiple people on one line `_test_kit_1_email`, …
   *
   * @param {Record<string, unknown>} properties
   * @returns {object[] | null}
   */
  function extractPersonsFromFlat(properties) {
    if (!properties || typeof properties !== "object") {
      return null;
    }
    const out = [];
    for (let i = 1; i <= 50; i++) {
      const pre = `_test_kit_${i}_`;
      const emailKey = `${pre}email`;
      const email = properties[emailKey];
      if (email == null || String(email).trim() === "") {
        break;
      }
      const row = {};
      TK_FIELDS.forEach((f) => {
        const v = properties[`${pre}${f}`];
        if (f === "same_as_delivery_address") {
          row[f] = v === true || v === "true";
        } else {
          row[f] = v != null ? String(v) : "";
        }
      });
      out.push(row);
    }
    return out.length ? out : null;
  }

  /**
   * Legacy `_test_kit_persons` JSON array.
   *
   * @param {Record<string, unknown>} properties
   * @returns {object[] | null}
   */
  function extractPersonsLegacyJson(properties) {
    const raw = properties._test_kit_persons;
    if (!raw || typeof raw !== "string") {
      return null;
    }
    try {
      const data = JSON.parse(raw);
      return Array.isArray(data) && data.length ? data : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * @param {Record<string, unknown>} properties
   * @returns {object[] | null}
   */
  function extractTestKitPersons(properties) {
    const props = normalizeCartLineProperties(properties);
    const lineUid = props._test_kit_line_uid ?? props["_test_kit_line_uid"];
    const hasLineUid = lineUid != null && String(lineUid).trim() !== "";
    if (hasLineUid) {
      const one = extractPersonsUnnumbered(props);
      return one && one.length ? one : [];
    }
    const one = extractPersonsUnnumbered(props);
    if (one) {
      return one;
    }
    const flat = extractPersonsFromFlat(props);
    if (flat) {
      return flat;
    }
    return extractPersonsLegacyJson(props);
  }

  /**
   * @param {string} s
   * @returns {string}
   */
  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  /**
   * @param {object} p
   * @returns {string}
   */
  function formatTestKitDisplayName(p) {
    const full = (p.full_name && String(p.full_name).trim()) || "";
    if (full) {
      return full;
    }
    const fn = (p.first_name && String(p.first_name).trim()) || "";
    const ln = (p.last_name && String(p.last_name).trim()) || "";
    return [fn, ln].filter(Boolean).join(" ") || "Person";
  }

  /**
   * @param {object} p
   * @returns {string}
   */
  function formatDobDisplay(p) {
    let dob = p.dob != null ? String(p.dob) : "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
      dob = `${dob.slice(8, 10)}/${dob.slice(5, 7)}/${dob.slice(0, 4)}`;
    }
    return dob.trim();
  }

  /**
   * @param {object[] | null} persons
   * @param {string} ctx drawer | page
   * @returns {string}
   */
  function renderTestKitListHtml(persons, ctx) {
    if (!persons || !persons.length) {
      return "";
    }
    const cls =
      ctx === "drawer"
        ? "cart-drawer__item-test-kit"
        : "cart-item__test-kit-persons";
    const items = persons
      .map((p) => {
        const name = formatTestKitDisplayName(p);
        const dob = formatDobDisplay(p);
        const dobHtml = dob
          ? `<span class="cart-test-kit-person__dob">Date of birth: ${escapeHtml(
              dob,
            )}</span>`
          : "";
        return `<li class="cart-test-kit-person"><span class="cart-test-kit-person__name">${escapeHtml(
          name,
        )}</span>${dobHtml}</li>`;
      })
      .join("");
    return `<ul class="${cls}">${items}</ul>`;
  }

  /**
   * Legacy holders: data-test-kit-persons JSON.
   *
   * @param {Element} holder
   */
  function hydrateEl(holder) {
    const raw = holder.getAttribute("data-test-kit-persons");
    const persons = raw ? extractPersonsLegacyJson({ _test_kit_persons: raw }) : null;
    if (!persons || !persons.length) {
      holder.innerHTML = "";
      holder.hidden = true;
      return;
    }
    holder.hidden = false;
    const ctx = holder.getAttribute("data-test-kit-context") || "drawer";
    holder.innerHTML = renderTestKitListHtml(persons, ctx);
  }

  function hydrateAll() {
    document.querySelectorAll("[data-test-kit-persons]").forEach(hydrateEl);
  }

  window.__betterYouNormalizeCartLineProperties = normalizeCartLineProperties;
  window.__betterYouExtractTestKitPersons = extractTestKitPersons;
  window.__betterYouTestKitListHtml = renderTestKitListHtml;
  window.__betterYouHydrateTestKitCartPersons = hydrateAll;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", hydrateAll);
  } else {
    hydrateAll();
  }

  document.addEventListener("cart:updated", hydrateAll);

  document.addEventListener("click", async (e) => {
    const btn = e.target.closest("[data-test-kit-edit]");
    if (!btn) {
      return;
    }
    if (window.testKitRegistrationConfig && window.testKitRegistrationConfig.enabled) {
      return;
    }
    e.preventDefault();
    const key = btn.getAttribute("data-item-key");
    if (!key) {
      return;
    }
    try {
      const r = await fetch("/cart.js");
      if (!r.ok) {
        return;
      }
      const cart = await r.json();
      const item = cart.items.find((i) => i.key === key);
      if (!item || !item.url) {
        return;
      }
      const u = new URL(item.url, window.location.origin);
      u.searchParams.set("test_kit_edit", "1");
      u.searchParams.set("key", key);
      window.location.href = u.toString();
    } catch (err) {
      console.error(err);
    }
  });
})();
